import { useQuery } from '@tanstack/react-query';

const BASE_URL = '/api';

// Search result types
export type SearchResultType =
  | 'load'
  | 'driver'
  | 'customer'
  | 'carrier'
  | 'truck'
  | 'trailer'
  | 'invoice'
  | 'settlement'
  | 'document';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  description?: string;
  url: string;
  metadata?: Record<string, string | number | boolean | undefined>;
  matchedFields?: string[];
  score: number;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  totalResults: number;
  searchTime: number; // in ms
  suggestions?: string[];
  categories: {
    type: SearchResultType;
    count: number;
    label: string;
  }[];
}

export interface RecentSearch {
  id: string;
  query: string;
  timestamp: string;
  resultCount: number;
}

export interface SearchSuggestion {
  query: string;
  type: 'recent' | 'popular' | 'autocomplete';
  category?: SearchResultType;
}

export interface GlobalSearchParams {
  query: string;
  types?: SearchResultType[];
  limit?: number;
  page?: number;
}

// Fetch functions
async function fetchGlobalSearch(params: GlobalSearchParams): Promise<SearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('q', params.query);
  if (params.types && params.types.length > 0) {
    params.types.forEach(t => searchParams.append('type', t));
  }
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.page) searchParams.set('page', params.page.toString());

  const res = await fetch(`${BASE_URL}/search?${searchParams.toString()}`);
  if (!res.ok) throw new Error('Failed to search');
  return res.json();
}

async function fetchRecentSearches(): Promise<RecentSearch[]> {
  const res = await fetch(`${BASE_URL}/search/recent`);
  if (!res.ok) throw new Error('Failed to fetch recent searches');
  return res.json();
}

async function fetchSearchSuggestions(query: string): Promise<SearchSuggestion[]> {
  const res = await fetch(`${BASE_URL}/search/suggestions?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error('Failed to fetch suggestions');
  return res.json();
}

async function saveRecentSearch(query: string): Promise<void> {
  await fetch(`${BASE_URL}/search/recent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
}

async function clearRecentSearches(): Promise<void> {
  await fetch(`${BASE_URL}/search/recent`, {
    method: 'DELETE',
  });
}

// Query hooks
export function useGlobalSearch(params: GlobalSearchParams) {
  return useQuery({
    queryKey: ['global-search', params],
    queryFn: () => fetchGlobalSearch(params),
    enabled: !!params.query && params.query.length >= 2,
    staleTime: 30000, // Cache for 30 seconds
  });
}

export function useRecentSearches() {
  return useQuery({
    queryKey: ['recent-searches'],
    queryFn: fetchRecentSearches,
  });
}

export function useSearchSuggestions(query: string) {
  return useQuery({
    queryKey: ['search-suggestions', query],
    queryFn: () => fetchSearchSuggestions(query),
    enabled: !!query && query.length >= 1,
    staleTime: 60000, // Cache for 1 minute
  });
}

// Helper functions
export { saveRecentSearch, clearRecentSearches };

// Type labels for display
export const SEARCH_TYPE_LABELS: Record<SearchResultType, string> = {
  load: 'Loads',
  driver: 'Drivers',
  customer: 'Customers',
  carrier: 'Carriers',
  truck: 'Trucks',
  trailer: 'Trailers',
  invoice: 'Invoices',
  settlement: 'Settlements',
  document: 'Documents',
};

// Type icons (for reference in components)
export const SEARCH_TYPE_ICONS: Record<SearchResultType, string> = {
  load: 'Package',
  driver: 'UserCircle',
  customer: 'Building2',
  carrier: 'Truck',
  truck: 'Truck',
  trailer: 'Container',
  invoice: 'FileText',
  settlement: 'DollarSign',
  document: 'File',
};

// URL generators for each type
export function getResultUrl(type: SearchResultType, id: string): string {
  switch (type) {
    case 'load': return `/loads/detail?id=${id}`;
    case 'driver': return `/drivers/detail?id=${id}`;
    case 'customer': return `/customers/detail?id=${id}`;
    case 'carrier': return `/carriers/detail?id=${id}`;
    case 'truck': return `/fleet/trucks/detail?id=${id}`;
    case 'trailer': return `/fleet/trailers/detail?id=${id}`;
    case 'invoice': return `/invoices/detail?id=${id}`;
    case 'settlement': return `/settlements/${id}`;
    case 'document': return `/documents?id=${id}`;
    default: return '/';
  }
}
